import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Button, Chip,
  Divider, Stack, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
  DialogActions, Switch, FormControlLabel, Snackbar, Alert, CircularProgress, useTheme,
  TextField, MenuItem, ListSubheader,
} from '@mui/material';
import {
  WorkspacePremiumRounded, AutorenewRounded, CreditCardRounded, CheckCircleRounded,
} from '@mui/icons-material';
import SubscriptionService from '../../services/subscription.service';
import PaymentGatewayService from '../../services/paymentGateway.service';
import { isB2bTc, canUseConnections, can } from '../../utils/roleUtils';
import { getBrandColor, getBrandContrastText, getBrandHover, getBrandHoverTint, getBrandTint } from '../../utils/colorUtils';

// Which tier is badged "Most popular" (a display choice, not data-driven).
const RECOMMENDED_TIER = 'BASIC';
// Stable display order so feature rows line up across cards.
const FEATURE_ORDER = ['ORG_COUNT', 'TC_USER_COUNT', 'INVOICE_MONTHLY', 'WORK_ORDER_MONTHLY', 'QUOTATION_MONTHLY',
  'MAX_B2B_CONNECTIONS', 'MAX_B2B_CLIENTS'];

const perLabel = (price) => {
  const unit = (price.period_unit || '').toLowerCase();
  return price.period_count === 1 ? `per ${unit}` : `per ${price.period_count} ${unit}s`;
};

// The two B2B connection caps are role-scoped: a connections-enabled TC (promoted, or an independent
// TC granted the feature) sees its partner cap, a B2B sees its client cap, others see neither —
// mirrors backend buildUsage.
const sortedEntitlements = (plan, canConnect, isB2b) =>
  [...(plan.entitlements || [])]
    .filter((e) => {
      if (e.feature_key === 'MAX_B2B_CONNECTIONS') return canConnect;
      if (e.feature_key === 'MAX_B2B_CLIENTS') return isB2b;
      return true;
    })
    .sort((a, b) => FEATURE_ORDER.indexOf(a.feature_key) - FEATURE_ORDER.indexOf(b.feature_key));

const entitlementText = (e, isB2b) =>
  `${e.unlimited ? 'Unlimited' : e.limit_value} ${SubscriptionService.getFeatureLabel(e.feature_key, isB2b)}`;

const Subscription = ({ currentUser }) => {
  const theme = useTheme();
  const isB2b = isB2bTc(currentUser);
  const canConnect = canUseConnections(currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [confirm, setConfirm] = useState(null); // { plan, price }
  const [autoRenewOptIn, setAutoRenewOptIn] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [savingAutoRenew, setSavingAutoRenew] = useState(false);
  const [gatewayFee, setGatewayFee] = useState({ feePercentage: 0, feeFixed: 0 });
  // BE-22: what pays — 'bank:<id>' or 'card:<id>'; renewals use the same account
  const [paySources, setPaySources] = useState({ banks: [], cards: [], cardsDenied: false });
  const [loadingSources, setLoadingSources] = useState(false);
  const [paySource, setPaySource] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Subscription actions follow the matrix (TC_ADMIN resolves all-true).
  const isTcAdmin = can(currentUser, 'subscription', 'write');
  const brandColor = getBrandColor(theme);
  const brandContrastText = getBrandContrastText(theme);
  const brandTint = getBrandTint(theme, 0.06);
  const brandHoverTint = getBrandHoverTint(theme, 0.12);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      SubscriptionService.getCurrent(),
      SubscriptionService.getPlans(),
    ])
      .then(([cur, planList]) => {
        setCurrent(cur);
        setPlans(Array.isArray(planList) ? planList : []);
      })
      .catch(() => notify('Failed to load subscription', 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    PaymentGatewayService.listEnabled()
      .then((list) => {
        const ng = (list || []).find((g) => g.gateway_key === 'NGENIUS');
        if (ng) setGatewayFee({ feePercentage: Number(ng.fee_percentage) || 0, feeFixed: Number(ng.fee_fixed) || 0 });
      })
      .catch(() => {});
  }, []);

  // Handle checkout return (?checkout=success|cancelled).
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      notify('Payment received — your subscription is being activated.', 'success');
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
      setTimeout(loadAll, 1500);
    } else if (checkout === 'cancelled') {
      notify('Checkout cancelled.', 'info');
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cycles = useMemo(() => {
    const seen = new Map();
    plans.forEach((p) => (p.prices || []).forEach((pr) => {
      const key = `${pr.period_unit}-${pr.period_count}`;
      if (!seen.has(key)) seen.set(key, { key, label: SubscriptionService.cycleLabel(pr) });
    }));
    return Array.from(seen.values());
  }, [plans]);

  useEffect(() => {
    if (!selectedCycle && cycles.length) setSelectedCycle(cycles[0].key);
  }, [cycles, selectedCycle]);

  const priceForCycle = (plan) =>
    (plan.prices || []).find((pr) => `${pr.period_unit}-${pr.period_count}` === selectedCycle);

  const openConfirm = (plan) => {
    const price = priceForCycle(plan);
    if (!price) { notify("This plan isn't available for the selected billing cycle", 'error'); return; }
    setAutoRenewOptIn(true);
    setPaySource('');
    setConfirm({ plan, price });
    setLoadingSources(true);
    SubscriptionService.getPaySources()
      .then(setPaySources)
      .catch(() => {})
      .finally(() => setLoadingSources(false));
  };

  const fee = useMemo(() => {
    if (!confirm) return 0;
    const base = Number(confirm.price.discounted_price ?? confirm.price.price) || 0;
    return Math.round((base * gatewayFee.feePercentage / 100 + gatewayFee.feeFixed) * 100) / 100;
  }, [confirm, gatewayFee]);

  const proceedToPayment = () => {
    if (!confirm) return;
    setPurchasing(true);
    const origin = window.location.origin;
    const [kind, sourceId] = paySource.split(':');
    SubscriptionService.purchase({
      plan_price_id: confirm.price.id,
      payment_gateway: 'NGENIUS',
      auto_renew: autoRenewOptIn,
      source_bank_account_id: kind === 'bank' ? sourceId : undefined,
      source_card_id: kind === 'card' ? sourceId : undefined,
      payment_redirect_url: `${origin}/settings/subscription?checkout=success`,
      payment_cancel_url: `${origin}/settings/subscription?checkout=cancelled`,
    })
      .then((resp) => {
        if (resp && resp.payment_url) {
          window.location.href = resp.payment_url;
        } else {
          notify('Subscription activated.', 'success');
          setConfirm(null);
          loadAll();
        }
      })
      .catch((e) => notify(e?.response?.data?.message || 'Payment could not be started', 'error'))
      .finally(() => setPurchasing(false));
  };

  const toggleAutoRenew = (enable) => {
    setSavingAutoRenew(true);
    const op = enable ? SubscriptionService.enableAutoRenew() : SubscriptionService.cancelAutoRenew();
    op.then(() => { notify(enable ? 'Auto-renewal enabled' : 'Auto-renewal cancelled'); loadAll(); })
      .catch((e) => notify(e?.response?.data?.message || 'Could not update auto-renewal', 'error'))
      .finally(() => setSavingAutoRenew(false));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Subscription</Typography>
      <Typography variant="caption" color="text.secondary">Manage your plan, usage and billing.</Typography>

      {/* Current plan + status */}
      <Card sx={{ mt: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${brandColor}` }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: brandColor, color: brandContrastText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WorkspacePremiumRounded />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current plan</Typography>
                <Typography variant="h5" sx={{ lineHeight: 1.2 }}>{current?.plan_name || 'Free'}</Typography>
                {current?.period_end && (
                  <Typography variant="caption" color="text.secondary">Renews / expires on {current.period_end}</Typography>
                )}
              </Box>
            </Stack>
            {current?.status && (
              <Chip label={SubscriptionService.getStatusLabel(current.status)} color={SubscriptionService.getStatusColor(current.status)} sx={{ fontWeight: 600 }} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Auto-renewal + saved card */}
      {(current?.saved_card || current?.auto_renew || current?.auto_renew_failed_on) && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: brandColor, color: brandContrastText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AutorenewRounded />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Auto-renewal</Typography>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{current?.auto_renew ? 'On' : 'Off'}</Typography>
                  {current?.auto_renew && current?.upcoming_renewal_date && !current?.auto_renew_failed_on && (
                    <Typography variant="caption" color="text.secondary">
                      Renews on {current.upcoming_renewal_date}
                      {current.upcoming_renewal_amount != null && ` · ${SubscriptionService.formatCurrency(current.upcoming_renewal_amount, current.upcoming_renewal_currency || 'AED')}`}
                    </Typography>
                  )}
                </Box>
              </Stack>
              {isTcAdmin && (current?.auto_renew ? (
                <Button variant="outlined" disabled={savingAutoRenew || !!current?.auto_renew_failed_on}
                  onClick={() => toggleAutoRenew(false)}
                  sx={{ fontWeight: 600, color: '#F44335', borderColor: '#F44335', '&:hover': { borderColor: '#c62828', bgcolor: 'rgba(244,67,53,0.06)' } }}>
                  Cancel auto-renewal
                </Button>
              ) : current?.saved_card ? (
                <Button variant="contained" disabled={savingAutoRenew || !!current?.auto_renew_failed_on}
                  onClick={() => toggleAutoRenew(true)} sx={{ bgcolor: brandColor, color: brandContrastText, '&:hover': { bgcolor: getBrandHover(theme) } }}>
                  Enable auto-renewal
                </Button>
              ) : null)}
            </Stack>

            {current?.auto_renew_failed_on && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Your plan lapsed on {current.auto_renew_failed_on} and automatic renewal didn't go through.
                Please renew your plan manually below to keep it active.
              </Alert>
            )}

            {current?.saved_card && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Stack direction="row" spacing={2} alignItems="center">
                  <CreditCardRounded sx={{ color: brandColor }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{current.saved_card.scheme || 'Card'} · {current.saved_card.masked_pan}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {current.saved_card.cardholder_name || ''}{current.saved_card.expiry ? ` · expires ${current.saved_card.expiry}` : ''}
                    </Typography>
                  </Box>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Plan picker */}
      {plans.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">Available plans</Typography>
              <Typography variant="caption" color="text.secondary">Pick the plan that fits your typing center — upgrade or downgrade anytime.</Typography>
            </Box>
            {cycles.length > 1 && (
              <ToggleButtonGroup size="small" exclusive value={selectedCycle} onChange={(e, v) => v && setSelectedCycle(v)}
                sx={{ '& .MuiToggleButton-root.Mui-selected': { bgcolor: brandColor, color: brandContrastText, '&:hover': { bgcolor: brandColor } } }}>
                {cycles.map((c) => <ToggleButton key={c.key} value={c.key} sx={{ textTransform: 'none', px: 2 }}>{c.label}</ToggleButton>)}
              </ToggleButtonGroup>
            )}
          </Stack>

          <Grid container spacing={2.5} alignItems="stretch">
            {plans.map((plan) => {
              const price = priceForCycle(plan);
              const isFreeTier = plan.tier === 'FREE';
              const isCurrent = current?.plan_id === plan.id && current?.status === 'ACTIVE';
              const recommended = plan.tier === RECOMMENDED_TIER;
              const hasDiscount = price && price.discounted_price != null && Number(price.discounted_price) < Number(price.price);
              const displayPrice = price ? (price.discounted_price ?? price.price) : null;
              const ctaLabel = current?.status === 'ACTIVE' ? 'Switch / Renew' : 'Subscribe';
              return (
                <Grid item xs={12} sm={6} md={4} key={plan.id} sx={{ display: 'flex' }}>
                  <Card variant="outlined" sx={{
                    width: '100%', display: 'flex', flexDirection: 'column', position: 'relative', borderRadius: 2,
                    borderWidth: recommended || isCurrent ? 2 : 1,
                    borderColor: isCurrent ? 'success.main' : recommended ? brandColor : 'divider',
                    boxShadow: recommended ? `0 12px 28px 0 ${getBrandTint(theme, 0.12)}` : 'none',
                    transition: 'all 0.25s ease',
                    '&:hover': { boxShadow: 8, transform: 'translateY(-6px)', borderColor: isCurrent ? 'success.main' : brandColor },
                  }}>
                    {recommended && (
                      <Box sx={{ position: 'absolute', top: 14, right: 14 }}>
                        <Chip label="Most popular" size="small" sx={{ bgcolor: brandColor, color: brandContrastText, fontWeight: 600 }} />
                      </Box>
                    )}
                    <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6">{plan.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minHeight: 34, display: 'block', mt: 0.5 }}>
                        {plan.description}
                      </Typography>

                      <Box sx={{ my: 2.5, minHeight: 80 }}>
                        {isFreeTier ? (
                          <>
                            <Typography variant="h4" fontWeight={700} sx={{ color: brandColor }}>Free</Typography>
                            <Typography variant="caption" color="text.secondary">forever</Typography>
                          </>
                        ) : price ? (
                          <>
                            {hasDiscount && (
                              <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary', lineHeight: 1 }}>
                                {SubscriptionService.formatCurrency(price.price, price.currency)}
                              </Typography>
                            )}
                            <Typography variant="h4" fontWeight={700} sx={{ color: brandColor, lineHeight: 1.2 }}>
                              {SubscriptionService.formatCurrency(displayPrice, price.currency)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{perLabel(price)}</Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>Not offered for this billing cycle</Typography>
                        )}
                      </Box>

                      <Divider sx={{ mb: 1.5 }} />

                      <Stack spacing={1.25} sx={{ flexGrow: 1 }}>
                        {sortedEntitlements(plan, canConnect, isB2b).map((e) => (
                          <Stack key={e.feature_key} direction="row" spacing={1} alignItems="center">
                            <CheckCircleRounded sx={{ fontSize: 18, color: 'success.main' }} />
                            <Typography variant="body2" color="text.secondary">{entitlementText(e, isB2b)}</Typography>
                          </Stack>
                        ))}
                      </Stack>

                      <Box sx={{ mt: 3 }}>
                        {isCurrent ? (
                          <Chip icon={<CheckCircleRounded />} label="Current plan" color="success" variant="outlined" sx={{ width: '100%', fontWeight: 600 }} />
                        ) : isFreeTier ? (
                          <Chip label="Base plan" variant="outlined" sx={{ width: '100%' }} />
                        ) : !price ? (
                          <Chip label="Unavailable" variant="outlined" sx={{ width: '100%' }} />
                        ) : isTcAdmin ? (
                          <Button fullWidth variant={recommended ? 'contained' : 'outlined'} onClick={() => openConfirm(plan)}
                            sx={recommended
                              ? { height: 40, bgcolor: brandColor, color: brandContrastText, '&:hover': { bgcolor: brandColor } }
                              : { height: 40, borderColor: brandColor, color: brandColor, '&:hover': { borderColor: brandColor, bgcolor: brandHoverTint } }}>
                            {ctaLabel}
                          </Button>
                        ) : null}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onClose={() => !purchasing && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm subscription</DialogTitle>
        <DialogContent>
          {confirm && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {confirm.plan.name} - {SubscriptionService.cycleLabel(confirm.price)}
              </Typography>
              <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">{SubscriptionService.formatCurrency(confirm.price.discounted_price ?? confirm.price.price, confirm.price.currency)}</Typography></Stack>
              {fee > 0 && (
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Platform fee</Typography>
                  <Typography variant="body2">{SubscriptionService.formatCurrency(fee, confirm.price.currency)}</Typography></Stack>
              )}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between"><Typography variant="subtitle2">Total</Typography>
                <Typography variant="subtitle2">{SubscriptionService.formatCurrency((Number(confirm.price.discounted_price ?? confirm.price.price) || 0) + fee, confirm.price.currency)}</Typography></Stack>

              {/* always shown: the server re-prices, so a plan that looked free may still need it (a 400 can be fixed here) */}
              <TextField select fullWidth size="small" label="Pay from" value={paySource} sx={{ mt: 2 }}
                  onChange={(e) => setPaySource(e.target.value)}
                  helperText={(Number(confirm.price.discounted_price ?? confirm.price.price) || 0) > 0
                    ? 'The card you pay with — recorded in your accounts against this. Renewals use the same.'
                    : 'Nothing to pay now — needed only if a price applies.'}
                >
                  {loadingSources && <MenuItem disabled value="">Loading accounts…</MenuItem>}
                  {!loadingSources && paySources.cardsDenied && (
                    <MenuItem disabled value="">Credit cards need finance access — ask your admin, or pay by bank</MenuItem>
                  )}
                  {paySources.cards.length > 0 && <ListSubheader>Credit cards</ListSubheader>}
                  {paySources.cards.map((c) => (
                    <MenuItem key={`card:${c.id}`} value={`card:${c.id}`}>
                      {c.card_name || 'Credit card'}{c.last_four ? ` ••${c.last_four}` : ''}
                    </MenuItem>
                  ))}
                  {paySources.banks.length > 0 && <ListSubheader>Debit card on bank account</ListSubheader>}
                  {paySources.banks.map((b) => (
                    <MenuItem key={`bank:${b.id}`} value={`bank:${b.id}`}>
                      {[b.bank_name, b.account_name].filter(Boolean).join(' - ') || 'Bank account'}
                    </MenuItem>
                  ))}
                  {!loadingSources && !paySources.cardsDenied && paySources.cards.length === 0 && paySources.banks.length === 0 && (
                    <MenuItem disabled value="">Add a bank account or credit card first</MenuItem>
                  )}
                </TextField>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: brandTint, border: '1px solid', borderColor: getBrandTint(theme, 0.25) }}>
                <FormControlLabel sx={{ alignItems: 'flex-start', m: 0 }}
                  control={<Switch checked={autoRenewOptIn} onChange={(e) => setAutoRenewOptIn(e.target.checked)} sx={{ mt: -0.5 }} />}
                  label={<Typography variant="caption" color="text.secondary">
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', display: 'block', mb: 0.25 }}>Enable auto-renewal</Box>
                    Securely save my card and renew automatically before expiry. The card is stored by the payment gateway, not Thaklis.
                  </Typography>} />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)} disabled={purchasing}>Cancel</Button>
          <Button variant="contained" onClick={proceedToPayment}
            disabled={purchasing || (!!confirm && (Number(confirm.price.discounted_price ?? confirm.price.price) || 0) > 0 && !paySource)}
            sx={{ bgcolor: brandColor, color: brandContrastText, '&:hover': { bgcolor: brandColor } }}>
            {purchasing ? 'Starting…' : 'Proceed to payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Subscription;
